export const SLICE04_EVENT_NAME = 'ownyou:slice04-readonly';
export const SLICE04_DEBUG_GLOBAL = '__OWNYOU_SLICE04_READONLY__';
export const SLICE04_CONSOLE_PREFIX = '[Slice04ReadOnly]';

export type Slice04MarkerKind =
  | 'route_render'
  | 'read_model_loaded'
  | 'helper_seed'
  | 'helper_cleanup'
  | 'no_spend_observation'
  | 'viewport_check';

export interface Slice04Marker {
  type: 'slice04_readonly_marker';
  version: 1;
  timestamp: number;
  kind: Slice04MarkerKind;
  route?: string;
  status: 'start' | 'success' | 'empty' | 'error';
  counts?: Record<string, number>;
  flags?: Record<string, boolean>;
  reasonCode?: string;
}

export interface Slice04DebugState {
  markers: Slice04Marker[];
  summarize: () => {
    markerCount: number;
    kinds: string[];
    countsByKind: Record<string, number>;
    latest?: Slice04Marker;
  };
  reset: () => void;
}

declare global {
  interface Window {
    __OWNYOU_SLICE04_READONLY__?: Slice04DebugState;
  }
}

const MAX_MARKERS = 300;
const SAFE_ROUTE_PATTERN = /^\/[a-z0-9/_-]*$/i;

function safeRoute(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return SAFE_ROUTE_PATTERN.test(value) ? value.slice(0, 96) : undefined;
}

function safeReason(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.replace(/[^\w:-]+/g, '_').slice(0, 96) || undefined;
}

function safeCounts(counts: Record<string, number | undefined | null> | undefined): Record<string, number> | undefined {
  if (!counts) return undefined;
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(counts)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const safeKey = key.replace(/[^\w:-]+/g, '_').slice(0, 48);
    if (safeKey) result[safeKey] = Math.max(0, Math.trunc(value));
  }
  return Object.keys(result).length ? result : undefined;
}

function safeFlags(flags: Record<string, boolean | undefined> | undefined): Record<string, boolean> | undefined {
  if (!flags) return undefined;
  const result: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(flags)) {
    if (typeof value !== 'boolean') continue;
    const safeKey = key.replace(/[^\w:-]+/g, '_').slice(0, 48);
    if (safeKey) result[safeKey] = value;
  }
  return Object.keys(result).length ? result : undefined;
}

function ensureDebugState(): Slice04DebugState | null {
  if (typeof window === 'undefined') return null;
  if (!window.__OWNYOU_SLICE04_READONLY__) {
    window.__OWNYOU_SLICE04_READONLY__ = {
      markers: [],
      summarize: () => {
        const markers = window.__OWNYOU_SLICE04_READONLY__?.markers ?? [];
        const countsByKind: Record<string, number> = {};
        for (const marker of markers) {
          countsByKind[marker.kind] = (countsByKind[marker.kind] ?? 0) + 1;
        }
        return {
          markerCount: markers.length,
          kinds: Object.keys(countsByKind).sort(),
          countsByKind,
          latest: markers[markers.length - 1],
        };
      },
      reset: () => {
        if (window.__OWNYOU_SLICE04_READONLY__) {
          window.__OWNYOU_SLICE04_READONLY__.markers = [];
        }
      },
    };
  }
  return window.__OWNYOU_SLICE04_READONLY__;
}

export function recordSlice04Marker(input: Omit<Slice04Marker, 'type' | 'version' | 'timestamp'> & { timestamp?: number }): Slice04Marker {
  const marker: Slice04Marker = {
    type: 'slice04_readonly_marker',
    version: 1,
    timestamp: input.timestamp ?? Date.now(),
    kind: input.kind,
    route: safeRoute(input.route),
    status: input.status,
    counts: safeCounts(input.counts),
    flags: safeFlags(input.flags),
    reasonCode: safeReason(input.reasonCode),
  };

  const debugState = ensureDebugState();
  if (debugState) {
    debugState.markers.push(marker);
    if (debugState.markers.length > MAX_MARKERS) {
      debugState.markers.splice(0, debugState.markers.length - MAX_MARKERS);
    }
    window.dispatchEvent(new CustomEvent(SLICE04_EVENT_NAME, { detail: marker }));
  }

  console.info(SLICE04_CONSOLE_PREFIX, marker);
  return marker;
}
